import { useCallback, useEffect, useMemo, useRef } from "react"
import { useSearchParams } from "react-router-dom"
import {
  ORDER_DISCLOSURE_OPEN_QUERY_KEY,
  ORDER_DISCLOSURE_SECTIONS,
  isOrderDisclosureSection,
  type OrderDisclosureSection,
} from "@/constants/order-details-disclosure"
import { trackEvent } from "@/utils/analytics"

export type { OrderDisclosureSection } from "@/constants/order-details-disclosure"

const ALL_SECTIONS_ORDERED = ORDER_DISCLOSURE_SECTIONS

function isLegacyExpandedParam(v: string | null): boolean {
  return v === "1" || v === "true"
}

function parseOpenList(raw: string | null): OrderDisclosureSection[] {
  if (!raw?.trim()) return []
  const seen = new Set<OrderDisclosureSection>()
  for (const part of raw.split(",")) {
    const id = part.trim().toLowerCase()
    if (isOrderDisclosureSection(id) && !seen.has(id)) seen.add(id)
  }
  return ALL_SECTIONS_ORDERED.filter((s) => seen.has(s))
}

function parseLegacyOpen(searchParams: URLSearchParams): Set<OrderDisclosureSection> {
  const set = new Set<OrderDisclosureSection>()
  for (const s of ALL_SECTIONS_ORDERED) {
    if (isLegacyExpandedParam(searchParams.get(s))) set.add(s)
  }
  return set
}

function parseOpenSetFromSearchParams(searchParams: URLSearchParams): Set<OrderDisclosureSection> {
  const openParam = searchParams.get(ORDER_DISCLOSURE_OPEN_QUERY_KEY)
  if (openParam != null && openParam.trim() !== "") {
    return new Set(parseOpenList(openParam))
  }
  return parseLegacyOpen(searchParams)
}

function serializeOpenSet(set: Set<OrderDisclosureSection>): string {
  return ALL_SECTIONS_ORDERED.filter((s) => set.has(s)).join(",")
}

function stripDisclosureQueryKeys(next: URLSearchParams): void {
  next.delete(ORDER_DISCLOSURE_OPEN_QUERY_KEY)
  ALL_SECTIONS_ORDERED.forEach((k) => next.delete(k))
}

function writeOpenToParams(next: URLSearchParams, set: Set<OrderDisclosureSection>): void {
  stripDisclosureQueryKeys(next)
  const serialized = serializeOpenSet(set)
  if (serialized) next.set(ORDER_DISCLOSURE_OPEN_QUERY_KEY, serialized)
}

export type UseOrderDetailsDisclosureParamsResult = {
  /** Current open sections (URL-backed). */
  openSections: ReadonlySet<OrderDisclosureSection>
  isOpen: (section: OrderDisclosureSection) => boolean
  setOpen: (section: OrderDisclosureSection, open: boolean) => void
  expandAll: () => void
  collapseAll: () => void
  allOpen: boolean
  anyOpen: boolean
}

/**
 * Syncs order-details progressive disclosure with `?open=items,billing,timeline` (comma-separated).
 * Clears disclosure state when `orderId` changes. Migrates legacy per-section `?items=1` params on read;
 * writes only `open` going forward.
 */
export function useOrderDetailsDisclosureParams(orderId: string | undefined): UseOrderDetailsDisclosureParamsResult {
  const [searchParams, setSearchParams] = useSearchParams()
  const mountedOrderRef = useRef<string | undefined>(undefined)
  const skipClearRef = useRef(true)

  const openSections = useMemo(
    () => parseOpenSetFromSearchParams(searchParams),
    [searchParams]
  )

  useEffect(() => {
    if (!orderId) return

    if (skipClearRef.current) {
      skipClearRef.current = false
      mountedOrderRef.current = orderId
      return
    }

    if (mountedOrderRef.current !== orderId) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          stripDisclosureQueryKeys(next)
          return next
        },
        { replace: true }
      )
      mountedOrderRef.current = orderId
    }
  }, [orderId, setSearchParams])

  const isOpen = useCallback(
    (section: OrderDisclosureSection) => openSections.has(section),
    [openSections]
  )

  const setOpen = useCallback(
    (section: OrderDisclosureSection, open: boolean) => {
      trackEvent("order_details_disclosure_section_toggle", {
        order_id: orderId ?? "",
        section,
        open,
      })
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          const set = parseOpenSetFromSearchParams(prev)
          if (open) set.add(section)
          else set.delete(section)
          writeOpenToParams(next, set)
          return next
        },
        { replace: true }
      )
    },
    [orderId, setSearchParams]
  )

  const expandAll = useCallback(() => {
    trackEvent("order_details_disclosure_expand_all", { order_id: orderId ?? "" })
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        writeOpenToParams(next, new Set(ALL_SECTIONS_ORDERED))
        return next
      },
      { replace: true }
    )
  }, [orderId, setSearchParams])

  const collapseAll = useCallback(() => {
    trackEvent("order_details_disclosure_collapse_all", { order_id: orderId ?? "" })
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        stripDisclosureQueryKeys(next)
        return next
      },
      { replace: true }
    )
  }, [orderId, setSearchParams])

  const allOpen = ALL_SECTIONS_ORDERED.every((s) => openSections.has(s))
  const anyOpen = ALL_SECTIONS_ORDERED.some((s) => openSections.has(s))

  return { openSections, isOpen, setOpen, expandAll, collapseAll, allOpen, anyOpen }
}
